import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Motion";
import { CalendarDays, Mail, Sparkles } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NextEvents() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const events = [];

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success(t("news.thanks"));
      setEmail("");
    } catch (err) {
      toast.error(t("talk.errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="agenda" className="py-16 md:py-24 bg-background" data-testid="events-section">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <Reveal>
          <div className="max-w-2xl">
            <div className="chip">{t("events.chip")}</div>
            <h2 className="mt-4 font-heading text-3xl md:text-5xl leading-[1.05]">
              {t("events.title_1")} <span className="text-primary">{t("events.title_rdv")}</span>
            </h2>
          </div>
        </Reveal>

        {events.length === 0 && (
          <Reveal delay={0.1}>
            <div
              className="mt-10 rounded-3xl p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center border-2 border-dashed"
              style={{ borderColor: "hsl(var(--accent))" }}
              data-testid="events-empty"
            >
              <div>
                <div
                  className="w-14 h-14 rounded-2xl grid place-items-center mb-5"
                  style={{ backgroundColor: "hsl(var(--accent))", color: "hsl(var(--foreground))" }}
                >
                  <CalendarDays className="w-7 h-7" strokeWidth={2.2} />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl leading-tight">
                  {t("events.empty_title")}
                </h3>
                <p className="mt-3 text-base md:text-lg text-foreground/75 leading-relaxed">
                  {t("events.empty_desc")}
                </p>
              </div>

              <form onSubmit={subscribe} className="w-full" data-testid="newsletter-form">
                <label htmlFor="newsletter-email" className="block text-sm font-bold text-foreground mb-2">
                  {t("events.newsletter_label")}
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    id="newsletter-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("events.newsletter_placeholder")}
                    data-testid="newsletter-email-input"
                    className="flex-1 px-4 py-3 rounded-full border-2 border-border bg-white focus:border-primary outline-none min-h-[48px]"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="newsletter-submit"
                    className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 justify-center"
                  >
                    <Mail className="w-4 h-4" strokeWidth={2.5} />
                    {busy ? "…" : t("events.newsletter_cta")}
                  </button>
                </div>
                <p className="mt-3 text-xs text-foreground/55 flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  {t("events.newsletter_note")}
                </p>
              </form>
            </div>
          </Reveal>
        )}
      </div>
    </section>
  );
}
